import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Circle, ExternalLink } from "lucide-react";
import { api, apiErr } from "../../lib/api";
import { SOCIAL_NETWORKS } from "../../lib/socials";
import { Share2 } from "lucide-react";

export default function AdminSocial() {
  const [local, setLocal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/settings")
      .then((r) => {
        const init = {};
        SOCIAL_NETWORKS.forEach((s) => { init[s.key] = r.data[s.key] || ""; });
        setLocal(init);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const activeCount = SOCIAL_NETWORKS.filter((s) => (local[s.key] || "").trim()).length;

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(SOCIAL_NETWORKS.map((s) => [s.key, (local[s.key] || "").trim()]));
      await api.put("/admin/settings", payload);
      toast.success("Réseaux sociaux enregistrés.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-social">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">Présence en ligne</div>
          <h1 className="mt-2 font-display text-3xl font-900 text-white">Réseaux sociaux</h1>
          <p className="mt-2 max-w-2xl text-sm text-ink-secondary">
            Renseignez l'adresse de vos comptes. Un réseau devient <span className="text-cyan">actif</span> dès qu'un lien est saisi
            et s'affiche alors dans le <span className="text-white">pied de page</span> et le <span className="text-white">bandeau du blog</span>.
          </p>
        </div>
        <div className="rounded-xl border border-white/10 bg-surface px-4 py-3 text-center" data-testid="social-active-count">
          <div className="font-display text-2xl font-900 tabular-nums text-cyan">{activeCount}<span className="text-ink-tertiary">/{SOCIAL_NETWORKS.length}</span></div>
          <div className="text-[10px] uppercase tracking-wider text-ink-tertiary">actifs</div>
        </div>
      </div>

      {/* Section « reseaux » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-reseaux">Réseaux sociaux</div>
      <div data-testid="social-panel-reseaux">
      {loading ? (
        <div className="mt-10 flex justify-center text-cyan"><Loader2 className="animate-spin" /></div>
      ) : (
        <>
          <div className="mt-8 space-y-3">
            {SOCIAL_NETWORKS.map((s) => {
              const val = (local[s.key] || "").trim();
              const active = !!val;
              return (
                <div key={s.key} className="flex flex-col gap-3 rounded-xl border border-white/10 bg-surface p-4 sm:flex-row sm:items-center" data-testid={`social-row-${s.key}`}>
                  <div className="flex w-full items-center gap-3 sm:w-52 sm:shrink-0">
                    <span className={`flex h-9 w-9 items-center justify-center rounded-full ${active ? "bg-cyan/15 text-cyan" : "bg-white/5 text-ink-tertiary"}`}>
                      <s.icon size={17} />
                    </span>
                    <span className="font-600 text-white">{s.label}</span>
                  </div>
                  <div className="relative flex-1">
                    <input
                      type="url" value={local[s.key] || ""} placeholder={s.placeholder}
                      onChange={(e) => setLocal({ ...local, [s.key]: e.target.value })}
                      data-testid={`social-input-${s.key}`}
                      className="w-full rounded-lg border border-white/10 bg-void px-3 py-2.5 pr-10 text-sm text-white outline-none transition-colors focus:border-cyan focus:ring-1 focus:ring-cyan"
                    />
                    {active && (
                      <a href={val} target="_blank" rel="noopener noreferrer" title="Ouvrir" data-testid={`social-open-${s.key}`}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-tertiary transition-colors hover:text-cyan">
                        <ExternalLink size={15} />
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 sm:w-28 sm:justify-end" data-testid={`social-status-${s.key}`}>
                    {active ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[11px] font-600 uppercase tracking-wider text-emerald-400">
                        <CheckCircle2 size={13} /> Actif
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-2.5 py-1 text-[11px] font-600 uppercase tracking-wider text-ink-tertiary">
                        <Circle size={12} /> Inactif
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="sticky bottom-4 mt-6 flex justify-end">
            <button onClick={save} disabled={saving} data-testid="social-save-all"
              className="aca-btn-primary inline-flex items-center gap-2 shadow-2xl disabled:opacity-60">
              {saving ? <><Loader2 size={15} className="animate-spin" /> Enregistrement…</> : "Enregistrer tous les réseaux"}
            </button>
          </div>
        </>
      )}
      </div>
      </>
    </div>
  );
}
